import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

// @ts-ignore - JSX コンポーネント（型定義なし）
import ListBlock from '../../../components/block/ListBlock';
import IconButton from '../../../components/button/IconButton';
import Input from '../../../components/form-item/Input';
import NumberInput from '../../../components/form-item/NumberInput';
import Select from '../../../components/form-item/Select';
import SwitchComponent from '../../../components/form-item/SwitchComponent';
import Toggle from '../../../components/form-item/Toggle';
import HelpHint from '../../../components/help-hint/HelpHint';
import { formatMaTelopText } from './buildMaTelopAss';
import {
  MA_TELOP_POSITIONS,
  MaTelopConfig,
  MaTelopPosition,
  applyDelimiterRule,
  isHexColor,
  resolveMaTelopTrackColor,
} from './maTelopConfig';
import { MA_TELOP_FONTS, resolveMaTelopFont } from './maTelopFonts';

/**
 * MA テロップ（タイムチャート焼き込み）の表示設定フォーム。
 *
 * 書き出しモーダルの「表示設定」で使う。将来プロジェクト設定画面にも
 * 同じフォームを置く前提で、値（MaTelopConfig）と onChange だけを受ける
 * 制御コンポーネントにしてある。
 *
 * 上部の見本は CSS で描画する（ffmpeg は使わない）。フォントは焼き込みと同じ
 * AXIS ROUND 50 を CSS の @font-face 経由で使うので、見た目は実際の出力に近い。
 */

export interface MaTelopConfigFieldsProps {
  value: MaTelopConfig;
  onChange: (_next: MaTelopConfig) => void;
  /** 見本のタイムスタンプ換算に使う fps（既定 30） */
  fps?: number;
}

// ===== 見本（CSS 描画） =====

/** 見本の土台。コンテナクエリ単位（cqw）で 1920px 基準の寸法をそのまま縮尺する */
const SampleFrame = styled.div`
  container-type: inline-size;
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0) 60%),
    #2f3133;
`;

/** 見本のテロップ列（基準端から積み上げる） */
const SampleStack = styled.div<{ $position: MaTelopPosition; $mx: number; $my: number }>`
  position: absolute;
  display: flex;
  flex-direction: ${p => (p.$position.startsWith('bottom') ? 'column-reverse' : 'column')};
  align-items: ${p => (p.$position.endsWith('right') ? 'flex-end' : 'flex-start')};
  /* 行間 4px（1080p 基準）を縮尺 */
  gap: calc(4 * 100cqw / 1920);
  ${p => (p.$position.startsWith('bottom') ? 'bottom' : 'top')}: calc(${p => p.$my} * 100cqw / 1920);
  ${p => (p.$position.endsWith('right') ? 'right' : 'left')}: calc(${p => p.$mx} * 100cqw / 1920);
  max-width: calc(100% - 2 * (${p => p.$mx} * 100cqw / 1920));
`;

const SampleLine = styled.span<{
  $font: string;
  $size: number;
  $color: string;
  $bg: string;
  $outline: number;
}>`
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  font-family: ${p => p.$font}, sans-serif;
  font-size: calc(${p => p.$size} * 100cqw / 1920);
  line-height: 1.2;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  /* BgBox の Outline 4px 相当のパディング */
  padding: calc(4 * 100cqw / 1920) calc(4 * 100cqw / 1920);
  ${p =>
    p.$outline > 0
      ? `text-shadow:
          calc(${p.$outline} * 100cqw / 1920) 0 0 #000,
          calc(${p.$outline} * -100cqw / 1920) 0 0 #000,
          0 calc(${p.$outline} * 100cqw / 1920) 0 #000,
          0 calc(${p.$outline} * -100cqw / 1920) 0 #000;`
      : ''}
`;

/** 見本に出すクリップ（焼き込みと同じ formatMaTelopText を通す。`_` 区切り短縮の効果が見える名前にしてある） */
const SAMPLE_CLIPS = [
  { startSeconds: 1.5, mediaName: 'SE_door_open', trackName: 'SFX', track: 1, gainDb: -3 },
  { startSeconds: 2.0, mediaName: 'BGM_1234_あたり', trackName: 'BGM', track: 2, gainDb: 0 },
] as const;

const TelopSample = ({ config, fps }: { config: MaTelopConfig; fps: number }) => {
  const font = resolveMaTelopFont(config.fontKey);
  const lines = SAMPLE_CLIPS.map(clip => ({
    // 焼き込み（buildMaTelopEntries）と同じく `_` 区切り短縮を通す
    text:
      formatMaTelopText(
        { ...clip, mediaName: applyDelimiterRule(clip.mediaName, config) },
        config,
        fps,
      ) || '（表示内容なし）',
    // 背景なし（無色）設定時は焼き込みと同様にボックスを描かない
    bg: config.showBackground
      ? resolveMaTelopTrackColor(config, clip.trackName, clip.track)
      : 'transparent',
  }));
  return (
    <SampleFrame aria-hidden="true">
      <SampleStack $position={config.position} $mx={config.marginX} $my={config.marginY}>
        {lines.map((line, i) => (
          <SampleLine
            key={i}
            $font={font.cssFamily}
            $size={config.fontSize}
            $color={config.fontColor}
            $bg={line.bg}
            $outline={config.textOutline ? config.outlineWidth : 0}
          >
            {line.text}
          </SampleLine>
        ))}
      </SampleStack>
    </SampleFrame>
  );
};

// ===== 数値入力の下書き管理 =====

/**
 * NumberInput は値を文字列で扱うため、入力途中（空欄など）で親の数値を
 * 上書きしないよう下書きを保持し、妥当な値のときだけ onCommit する
 */
function useNumberDraft(value: number, onCommit: (_n: number) => void, allowDecimal = false) {
  const [draft, setDraft] = useState<string | null>(null);
  useEffect(() => setDraft(null), [value]);
  const text = draft ?? String(value);
  const handleChange = (e: { target: { value: string } }) => {
    const raw = e.target.value;
    setDraft(raw);
    const n = allowDecimal ? parseFloat(raw) : parseInt(raw, 10);
    if (Number.isFinite(n) && n >= 0) onCommit(n);
  };
  return { text, handleChange };
}

// ===== トラック色 =====

/** ネイティブのカラーピッカー（グローバル部品が無いため最小限のラッパー） */
const ColorSwatch = styled.input.attrs({ type: 'color' })`
  width: 36px;
  height: 36px;
  padding: 2px;
  border: 1px solid var(--soremo-border, #d9dcde);
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  flex-shrink: 0;
`;

const TrackColorRow = ({
  track,
  color,
  onChange,
  onDelete,
}: {
  track: string;
  color: string;
  onChange: (_next: { track: string; color: string }) => void;
  onDelete: () => void;
}) => (
  <div className="u-row u-gap8 u-w100">
    <div className="u-fill">
      <Input
        type="text"
        placeholder="トラック名（例: BGM）"
        value={track}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange({ track: e.target.value, color })}
      />
    </div>
    <ColorSwatch
      value={color.toLowerCase()}
      onChange={(e: ChangeEvent<HTMLInputElement>) =>
        onChange({ track, color: e.target.value.toUpperCase() })
      }
      aria-label="背景色"
    />
    <IconButton icon="delete" onClick={onDelete} />
  </div>
);

// ===== 本体 =====

const MaTelopConfigFields = ({ value, onChange, fps = 30 }: MaTelopConfigFieldsProps) => {
  const update = (patch: Partial<MaTelopConfig>) => onChange({ ...value, ...patch });

  const fontSize = useNumberDraft(value.fontSize, n => update({ fontSize: Math.max(1, n) }));
  const marginX = useNumberDraft(value.marginX, n => update({ marginX: n }));
  const marginY = useNumberDraft(value.marginY, n => update({ marginY: n }));
  const outlineWidth = useNumberDraft(value.outlineWidth, n => update({ outlineWidth: n }));
  const fixedSeconds = useNumberDraft(
    value.fixedDurationSeconds,
    n => update({ fixedDurationSeconds: Math.max(0.1, n) }),
    true,
  );
  const nameSegmentCount = useNumberDraft(value.nameSegmentCount, n =>
    update({ nameSegmentCount: Math.min(20, Math.max(1, Math.round(n))) }),
  );

  const fontOptions = useMemo(
    () => MA_TELOP_FONTS.map(font => ({ value: font.key, name: font.label })),
    [],
  );
  const positionOptions = useMemo(
    () => MA_TELOP_POSITIONS.map(p => ({ value: p.value, name: p.label })),
    [],
  );

  const updateTrackColor = (index: number, next: { track: string; color: string }) => {
    const trackColors = value.trackColors.map((entry, i) => (i === index ? next : entry));
    update({ trackColors });
  };

  return (
    <div className="u-col u-gap16 u-w100">
      <TelopSample config={value} fps={fps} />

      <div className="u-row u-gap8 u-w100">
        <Select
          label="フォント"
          value={value.fontKey}
          options={fontOptions}
          onChange={e => update({ fontKey: e.target.value })}
        />
        <Select
          label="位置"
          value={value.position}
          options={positionOptions}
          onChange={e => update({ position: e.target.value as MaTelopPosition })}
        />
      </div>

      <div className="u-col u-gap8 u-w100">
        <div className="u-row u-gap4">
          <span className="bodytext-11">寸法</span>
          <HelpHint description="1920×1080 基準の px で指定します。動画サイズに合わせて自動で縮尺されます" />
        </div>
        <div className="u-row u-gap8 u-w100">
          <NumberInput
            label="文字サイズ"
            suffix="px"
            value={fontSize.text}
            onChange={fontSize.handleChange}
          />
          <NumberInput
            label="余白（横）"
            suffix="px"
            value={marginX.text}
            onChange={marginX.handleChange}
          />
          <NumberInput
            label="余白（縦）"
            suffix="px"
            value={marginY.text}
            onChange={marginY.handleChange}
          />
        </div>
      </div>

      <div className="u-col u-gap8 u-w100">
        <span className="bodytext-11">表示内容</span>
        <Toggle
          value={value.showTimestamp}
          onClick={() => update({ showTimestamp: !value.showTimestamp })}
        >
          <span className="bodytext-13">タイムスタンプ</span>
        </Toggle>
        {value.showTimestamp && (
          <SwitchComponent
            value={value.timestampFormat}
            options={[
              { value: 'timecode', label: 'M:SS.FF' },
              { value: 'frames', label: '絶対フレーム' },
            ]}
            onChange={v => v && update({ timestampFormat: v })}
          />
        )}
        <Toggle
          value={value.showMediaName}
          onClick={() => update({ showMediaName: !value.showMediaName })}
        >
          <span className="bodytext-13">メディア名</span>
        </Toggle>
        {value.showMediaName && (
          <>
            <div className="u-row u-gap4 u-align-center u-w100">
              <Toggle
                value={value.nameDelimiterEnabled}
                onClick={() => update({ nameDelimiterEnabled: !value.nameDelimiterEnabled })}
              >
                <span className="bodytext-13">_ 区切りで短縮</span>
              </Toggle>
              <HelpHint description={'メディア名を _ で区切り、先頭から指定の個数まで表示します\n（例: BGM_1234_あたり → 表示範囲 2 → BGM_1234）\n表示名 CSV で置き換えた名前にも適用されます'} />
            </div>
            {value.nameDelimiterEnabled && (
              <NumberInput
                label="表示範囲（区切り数）"
                value={nameSegmentCount.text}
                onChange={nameSegmentCount.handleChange}
              />
            )}
          </>
        )}
        <Toggle
          value={value.showTrackName}
          onClick={() => update({ showTrackName: !value.showTrackName })}
        >
          <span className="bodytext-13">トラック名</span>
        </Toggle>
        <Toggle value={value.showGain} onClick={() => update({ showGain: !value.showGain })}>
          <span className="bodytext-13">ゲイン（0dB 以外）</span>
        </Toggle>
      </div>

      <div className="u-col u-gap8 u-w100">
        <span className="bodytext-11">表示時間</span>
        <SwitchComponent
          value={value.durationMode}
          options={[
            { value: 'clip', label: 'クリップの長さ' },
            { value: 'fixed', label: '固定' },
          ]}
          onChange={v => v && update({ durationMode: v })}
        />
        {value.durationMode === 'fixed' && (
          <NumberInput
            label="表示秒数"
            suffix="秒"
            allowDecimal
            value={fixedSeconds.text}
            onChange={fixedSeconds.handleChange}
          />
        )}
      </div>

      <div className="u-col u-gap8 u-w100">
        <span className="bodytext-11">文字</span>
        <div className="u-row u-gap8 u-w100">
          <ColorSwatch
            value={value.fontColor.toLowerCase()}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              isHexColor(e.target.value) && update({ fontColor: e.target.value.toUpperCase() })
            }
            aria-label="文字色"
          />
          <Toggle
            value={value.textOutline}
            onClick={() => update({ textOutline: !value.textOutline })}
          >
            <span className="bodytext-13">黒の縁取り</span>
          </Toggle>
        </div>
        {value.textOutline && (
          <NumberInput
            label="縁取り幅"
            suffix="px"
            value={outlineWidth.text}
            onChange={outlineWidth.handleChange}
          />
        )}
      </div>

      <div className="u-col u-gap8 u-w100">
        <span className="bodytext-11">背景色</span>
        <Toggle
          value={value.showBackground}
          onClick={() => update({ showBackground: !value.showBackground })}
        >
          <span className="bodytext-13">トラック別の背景色を表示（OFF = 無色）</span>
        </Toggle>
        {value.showBackground && (
          <>
            {value.trackColors.map((entry, index) => (
              <TrackColorRow
                key={index}
                track={entry.track}
                color={entry.color}
                onChange={next => updateTrackColor(index, next)}
                onDelete={() =>
                  update({ trackColors: value.trackColors.filter((_, i) => i !== index) })
                }
              />
            ))}
            <ListBlock
              type="add"
              label="トラック色を追加"
              onClick={() =>
                update({
                  trackColors: [
                    ...value.trackColors,
                    { track: '', color: value.defaultTrackColor },
                  ],
                })
              }
            />
            <div className="u-row u-gap8 u-w100">
              <ColorSwatch
                value={value.defaultTrackColor.toLowerCase()}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  isHexColor(e.target.value) &&
                  update({ defaultTrackColor: e.target.value.toUpperCase() })
                }
                aria-label="その他のトラックの背景色"
              />
              <span className="bodytext-13">その他のトラック</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MaTelopConfigFields;
